import { useState } from 'react';
import { Lock, Crown } from 'lucide-react';
import { useSubscription, FEATURE_INFO } from '../../hooks/useSubscription';
import PaywallModal from './PaywallModal';

/**
 * FeatureGate - Wraps content that requires Pro subscription
 *
 * Usage:
 * <FeatureGate feature={PRO_FEATURES.GOALS}>
 *   <GoalsContent />
 * </FeatureGate>
 *
 * Props:
 * - feature: The PRO_FEATURES key for the feature being gated
 * - children: The content to show when user has access
 * - fallback: Optional custom fallback component
 * - showLockedOverlay: If true, shows content with locked overlay instead of replacing it
 */
export default function FeatureGate({
  feature,
  children,
  fallback = null,
  showLockedOverlay = false,
}) {
  const { hasAccess } = useSubscription();
  const [showPaywall, setShowPaywall] = useState(false);

  const hasFeatureAccess = hasAccess(feature);

  if (hasFeatureAccess) {
    return children;
  }

  if (fallback) {
    return fallback;
  }

  if (showLockedOverlay) {
    return (
      <div className="relative">
        <div className="opacity-50 pointer-events-none select-none">
          {children}
        </div>
        <button
          onClick={() => setShowPaywall(true)}
          className="absolute inset-0 flex items-center justify-center bg-surface-base/80 backdrop-blur-sm"
        >
          <div className="text-center space-y-3">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-surface-card border border-border-default">
              <Lock className="w-5 h-5 text-text-tertiary" />
            </div>
            <p className="text-sm text-text-secondary">Pro Feature</p>
            <button className="px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent/90 transition-colors">
              Unlock
            </button>
          </div>
        </button>

        <PaywallModal
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          feature={feature}
        />
      </div>
    );
  }

  // Default locked view
  const featureInfo = FEATURE_INFO[feature];

  return (
    <>
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-surface-card border border-border-default flex items-center justify-center mb-4">
          <Lock className="w-7 h-7 text-text-tertiary" />
        </div>

        <h3 className="text-xl font-semibold text-text-primary mb-2">
          {featureInfo?.name || 'Pro Feature'}
        </h3>

        <p className="text-text-secondary mb-6 max-w-sm">
          {featureInfo?.description || 'This feature requires a Pro subscription.'}
        </p>

        <button
          onClick={() => setShowPaywall(true)}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-accent to-orange-500 text-white font-medium rounded-xl hover:opacity-90 transition-opacity"
        >
          <Crown className="w-4 h-4" />
          Unlock with Pro
        </button>
      </div>

      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        feature={feature}
      />
    </>
  );
}

/**
 * useFeatureGate - Hook for programmatic feature gating
 *
 * Usage:
 * const { canAccess, showPaywall, PaywallModal } = useFeatureGate(PRO_FEATURES.GOALS);
 *
 * const handleClick = () => {
 *   if (!canAccess) {
 *     showPaywall();
 *     return;
 *   }
 *   // Do the thing
 * };
 */
export function useFeatureGate(feature) {
  const { hasAccess } = useSubscription();
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);

  const canAccess = hasAccess(feature);

  const PaywallModalComponent = () => (
    <PaywallModal
      isOpen={isPaywallOpen}
      onClose={() => setIsPaywallOpen(false)}
      feature={feature}
    />
  );

  return {
    canAccess,
    showPaywall: () => setIsPaywallOpen(true),
    hidePaywall: () => setIsPaywallOpen(false),
    PaywallModal: PaywallModalComponent,
  };
}
